/**
 * useAutoCompare — starts Compare with Thrive by itself for the lists the
 * Combine page needs checked (task 2026-09-24-combine-more-filters, batch 2:
 * "remove the Compare with Thrive click"). The recruiter no longer decides
 * to spend Thrive calls; selecting lists with "Remove contacts already in
 * Thrive" on does. Two rules bound that:
 *
 * - **Debounce.** Nothing starts until the selection, the mode and the
 *   target search (`selectionKey`) have been stable for `delayMs`
 *   (`AUTO_COMPARE_DELAY_MS`), and only while the preview on screen answers
 *   the current recipe (`ready`) — so clicking through lists fires nothing,
 *   and no run starts on a previous recipe's Compare states.
 * - **Once per list per target and mode (`scopeKey`).** A list is started
 *   at most once for one confirmed target search and removal mode, across
 *   re-renders, re-selecting it, and a failure (the page's "Try again" is
 *   the only retry). A new target or mode is a new chance (epic
 *   2026-10-01-combine-stability-perf, sub-task 02: the old once-per-session
 *   rule left a list "Not checked · Compare again" after the target
 *   changed). Following spends no Thrive call, so it does not use up the
 *   start: a list whose followed run ended without making it checked (a run
 *   on a selection, say) can still be started once (/pr-review iter 1 of
 *   PR #965).
 * - **Follow what nothing polls.** `followIds` leaves out the lists the
 *   page is already polling (`autoCompareIds`). A list is followed once
 *   while it stays in `followIds`; once it leaves (the page polls it, or
 *   the run ended) and comes back, it is a new run nobody polls — started
 *   elsewhere, even on a list this page started or followed before — and
 *   is followed again (/pr-review iter 2 of PR #965, Agent A §3.1).
 *
 * `start` / `follow` are `useCompareSources().start` / `.track`.
 */
import { useEffect, useRef, useState } from "react";

/** How long the selection must stay still before a Compare starts. */
export const AUTO_COMPARE_DELAY_MS = 1500;

export interface UseAutoCompareArgs {
  /** Changes whenever the selected lists, the mode or the target change. */
  selectionKey: string;
  /** The confirmed target and the mode: a list is started once per value. */
  scopeKey?: string;
  /** Lists to start (`autoCompareIds(rows, progress).start`). */
  startIds: readonly string[];
  /** Lists whose run is already going and that the page does not poll yet,
   *  to follow (`autoCompareIds(rows, progress).follow`). */
  followIds: readonly string[];
  /** The preview on screen answers the current recipe. */
  ready: boolean;
  start: (importIds: readonly string[]) => void;
  follow: (importIds: readonly string[]) => void;
  delayMs?: number;
}

export function useAutoCompare({
  selectionKey,
  scopeKey = "",
  startIds,
  followIds,
  ready,
  start,
  follow,
  delayMs = AUTO_COMPARE_DELAY_MS,
}: UseAutoCompareArgs): void {
  const startedRef = useRef<Set<string>>(new Set());
  const followedRef = useRef<Set<string>>(new Set());
  const [settledKey, setSettledKey] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setSettledKey(selectionKey), delayMs);
    return () => clearTimeout(timer);
  }, [selectionKey, delayMs]);

  const settled = settledKey === selectionKey;
  // Joined so the effect below runs when the lists change, not on every
  // render's new array.
  const startKey = startIds.join(",");
  const followKey = followIds.join(",");

  useEffect(() => {
    const followed = followedRef.current;
    const listed = new Set(followKey ? followKey.split(",") : []);
    // Forgotten as soon as it leaves the list, even while not ready, so a
    // later run on it is followed.
    for (const id of followed) if (!listed.has(id)) followed.delete(id);
    if (!settled || !ready) return;
    const started = startedRef.current;
    const scoped = (id: string) => `${scopeKey}|${id}`;
    const toStart = startKey ? startKey.split(",").filter((id) => !started.has(scoped(id))) : [];
    const toFollow = [...listed].filter((id) => !followed.has(id));
    for (const id of toStart) started.add(scoped(id));
    for (const id of toFollow) followed.add(id);
    if (toStart.length > 0) start(toStart);
    if (toFollow.length > 0) follow(toFollow);
  }, [settled, ready, startKey, followKey, start, follow, scopeKey]);
}
